import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import User from "../database/User";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const token = searchParams.get("token");

    // Check if user is already authenticated
    useEffect(() => {
        if (User.isAuthenticated) {
            navigate("/");
        }
    }, [navigate]);

    // Password reset form state
    const [resetForm, setResetForm] = useState({
        password: "",
        confirmPassword: "",
    });

    const updateResetForm = (field, value) => {
        setResetForm((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear errors when user starts typing
        if (error) setError("");
        if (success) setSuccess("");
    };

    // Handle password reset submission
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validate passwords match
        if (resetForm.password !== resetForm.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        // Validate password strength
        if (resetForm.password.length < 7 || !/[A-Z]/.test(resetForm.password) || !/[0-9]/.test(resetForm.password)) {
            setError("Password must be at least 7 characters and contain at least one uppercase letter and one number");
            return;
        }

        if (!token) {
            setError("Invalid or missing reset token");
            return;
        }

        setLoading(true);

        try {
            const response = await User.resetPassword(
                token,
                resetForm.password
            );
            setSuccess(
                "Password reset successful! Redirecting to login page..."
            );
            setResetForm({
                password: "",
                confirmPassword: "",
            });

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (err) {
            console.error("Password reset error:", err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    "Failed to reset password. The link may have expired."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
            <Card className="w-full max-w-md shadow-lg">
                <CardHeader>
                    <CardTitle>Reset Password</CardTitle>
                    <CardDescription>
                        Enter your new password below
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handlePasswordReset}>
                    <CardContent className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {success && (
                            <Alert>
                                <AlertDescription>{success}</AlertDescription>
                            </Alert>
                        )}

                        {!token && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    Invalid or missing reset token. Please
                                    request a new password reset link.
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="password">New Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter new password"
                                value={resetForm.password}
                                onChange={(e) =>
                                    updateResetForm("password", e.target.value)
                                }
                                required
                                disabled={loading || !token}
                                minLength={7}
                            />
                            <p className="text-xs text-muted-foreground">
                                Password must be at least 7 characters, with one uppercase letter and one number
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">
                                Confirm New Password
                            </Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                placeholder="Confirm new password"
                                value={resetForm.confirmPassword}
                                onChange={(e) =>
                                    updateResetForm(
                                        "confirmPassword",
                                        e.target.value
                                    )
                                }
                                required
                                disabled={loading || !token}
                                minLength={7}
                            />
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col space-y-2">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={loading || !token}
                        >
                            {loading ? "Resetting..." : "Reset Password"}
                        </Button>
                        <button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="text-sm text-muted-foreground hover:text-primary underline"
                        >
                            Back to Login
                        </button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}
